import { HttpError } from './http-error'
import { ScanUnavailableError, type ScanClient } from './scan-client'
import type { SubmitScanRequest } from './types'

/**
 * The backoff between submit attempts, in milliseconds. Three retries after the first try, doubling.
 *
 * Bounded, and short. The patient is sitting in front of a screen that says nothing useful while
 * this runs, so the ceiling is fourteen seconds of waiting rather than a minute of it — long enough
 * to outlast a task rolling behind the load balancer, short enough that giving up and telling them
 * so is still the better answer.
 */
export const SUBMIT_BACKOFF_MS = [2_000, 4_000, 8_000] as const

/** Whether this failure is one a second attempt could plausibly answer differently. */
function isWorthRetrying(error: unknown): boolean {
  // The service answers 404 for "no such assessment" and for "terminal or past expiry" alike, and
  // neither changes by being asked again. Retrying it would keep a patient waiting for an outcome
  // that is already decided.
  if (error instanceof ScanUnavailableError) {
    return false
  }

  // A 4xx is this request being wrong, and it will be just as wrong next time. Anything at or above
  // 500 is the service having a bad moment.
  if (error instanceof HttpError) {
    return error.status >= 500
  }

  // Everything else reaching here is fetch itself rejecting: no network, DNS, a connection dropped
  // mid-flight. That is the case this retry exists for most of all — a patient on a phone walking
  // between two cells at the wrong moment.
  return true
}

/**
 * Submits a measured scan, retrying a transient failure rather than discarding the reading.
 *
 * The scan is fifty-five seconds of the patient holding still in front of a camera, so losing its
 * result to one 5xx costs them the whole thing again. The service is first-wins idempotent on
 * submit — a second call with the same token records nothing new and answers success — so a retry
 * can only ever store one reading, which is what makes this safe rather than merely kind.
 *
 * @param scan The scan client.
 * @param accessToken The scoped token `verify` returned.
 * @param request The measured result, already shaped for the wire.
 * @param sleep How to wait between attempts; injected so a test drives it.
 * @param isCancelled Whether the flow has moved on, checked between attempts.
 */
export async function submitWithRetry(
  scan: Pick<ScanClient, 'submitScan'>,
  accessToken: string,
  request: SubmitScanRequest,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  isCancelled: () => boolean = () => false,
): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await scan.submitScan(accessToken, request)
      return
    } catch (error) {
      if (attempt >= SUBMIT_BACKOFF_MS.length || !isWorthRetrying(error)) {
        throw error
      }

      await sleep(SUBMIT_BACKOFF_MS[attempt])

      if (isCancelled()) {
        // The patient closed the page or the flow moved on. Rethrowing here would set state on an
        // unmounted tree; the caller's own cancelled check swallows it.
        throw error
      }
    }
  }
}
